import { HPONode } from '@/features/terminology/hpo/hpoTypes'

// Type definitions for term structures
export interface Term {
  id: string
  label: string
  description?: string
  category: 'Key' | 'Additional' | 'Excluded'
}

export interface TermsByCategory {
  Key: Term[]
  Additional: Term[]
  Excluded: Term[]
}

export enum Sex {
  Male = 'male',
  Female = 'female',
  Ambiguous = 'ambiguous',
  Unknown = '',
}

export enum TestType {
  urWGS_Patient_Only = 'Ultra-Rapid Whole Genome Sequencing',
  urWGS_Duo = 'Ultra-Rapid Whole Genome Sequencing Duo',
  urWGS_Trio = 'Ultra-Rapid Whole Genome Sequencing Trio',
  urWGS_Quad = 'Ultra-Rapid Whole Genome Sequencing Quad',
  rWGS_Patient_Only = 'rWGS Proband Only',
  rWGS_Duo = 'rWGS Duo',
  rWGS_Trio = 'rWGS Trio',
  rWGS_Quad = 'rWGS Quad',
  NONE = '',
}

export enum CollectionType {
  WholeBloodEDTA = 'whole-blood-edta',
  Buccal = 'buccal',
  Saliva = 'saliva',
  // Not yet supported in the UI; reserved for future use.
  DNA = 'dna',
  Unknown = '',
}

export function getCollectionTypeLabel(collectionType: CollectionType): string {
  switch (collectionType) {
    case CollectionType.WholeBloodEDTA:
      return 'Whole Blood (EDTA)'
    case CollectionType.Buccal:
      return 'Buccal'
    case CollectionType.Saliva:
      return 'Saliva (confirmatory testing only)'
    default:
      return ''
  }
}

/** Serialized phenotypic trait (API / canonical). Legacy display strings are accepted via {@link normalizePhenotypicTrait}. */
export enum PhenotypicTrait {
  Affected = 'affected',
  Unaffected = 'unaffected',
  PartiallyAffected = 'partially_affected',
  Unknown = 'unknown',
}

export function getPhenotypicTraitLabel(trait: PhenotypicTrait): string {
  switch (trait) {
    case PhenotypicTrait.Affected:
      return 'Affected'
    case PhenotypicTrait.Unaffected:
      return 'Unaffected'
    case PhenotypicTrait.PartiallyAffected:
      return 'Partially Affected'
    case PhenotypicTrait.Unknown:
      return 'Unknown'
    default:
      return ''
  }
}

/** Maps persisted / legacy strings to {@link PhenotypicTrait}. */
export function normalizePhenotypicTrait(raw: string | null | undefined): PhenotypicTrait {
  const s = (raw ?? '').trim()
  if (!s) {
    return PhenotypicTrait.Unknown
  }

  if (
    s === PhenotypicTrait.Affected ||
    s === PhenotypicTrait.Unaffected ||
    s === PhenotypicTrait.PartiallyAffected ||
    s === PhenotypicTrait.Unknown
  ) {
    return s as PhenotypicTrait
  }

  // Legacy human-readable labels (pre–snake_case API)
  if (s === 'Clinically Affected') {
    return PhenotypicTrait.Affected
  }
  if (s === 'Clinically Unaffected') {
    return PhenotypicTrait.Unaffected
  }

  // Legacy enum member names / stray identifiers
  const lower = s.toLowerCase()
  if (lower === 'clinicallyaffected' || lower === 'affected') {
    return PhenotypicTrait.Affected
  }
  if (lower === 'clinicallyunaffected' || lower === 'unaffected') {
    return PhenotypicTrait.Unaffected
  }
  if (lower === 'partially_affected' || lower === 'partially affected') {
    return PhenotypicTrait.PartiallyAffected
  }

  return PhenotypicTrait.Unknown
}

/** Badge / summary: “clinically affected” presentation includes partial penetrance. */
export function phenotypicTraitShowsAffectedPresentation(trait: PhenotypicTrait): boolean {
  return trait === PhenotypicTrait.Affected || trait === PhenotypicTrait.PartiallyAffected
}

// Explicit form data interfaces
export interface OrderData {
  selectedOption: TestType
  lab_name: string
  /**
   * Epic Contact Serial Number for the encounter that produced this order.
   *
   * Populated server-side on GET by ``imdb_client._resolve_lab_info`` from
   * ``imdb.order.encounter_csn`` (snake_case — matches the IMDB column and the
   * Python ``OrderInfo`` model in ``imdb_client.submitted``). Never authored by
   * the SPA: serves only as a read-back fallback (e.g. ``OrderInProgress.tsx``
   * resubmit when canonical ``canonicalOrder.order.encounterCsn`` is empty).
   *
   * The canonical write-side mirror is camelCase ``canonicalOrder.order.encounterCsn``
   * — see ``src/ordering/CANONICAL_FIELD_MATRIX.md``.
   */
  encounter_csn?: string | null
}

export interface PatientData {
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  sex: Sex
  mrn: string
  collectionType: CollectionType
  specimenId?: string
  race?: string
  ethnicity?: string
}

export interface ClinicalData {
  clinicalNote: string
  differentialDiagnosis?: string
  priorGeneticTesting?: string
  construeResults?: {
    terms?: Array<{ id: string; label: string; description?: string }>
    message?: string
    success?: boolean
  }
}

export interface CodeableConcept {
  id: string
  label: string
  description?: string
  category: 'Key' | 'Additional' | 'Excluded'
}

// HPO Option interface for the search component
export interface HPOOption {
  id: string
  label: string
  node: HPONode
}

// Extended HPO option with category and source tracking
export interface CategorizedHPOOption extends HPOOption {
  category: 'Key' | 'Additional' | 'Excluded'
  /** Tracks whether term was extracted from clinical note or manually added by user */
  source?: 'extracted' | 'unknown' | 'manual' | 'phenoml' | 'other'

  otherSource?: string
}

export interface ReasonForTestingData {
  clinicalDiagnosis: CategorizedHPOOption[]
  icd10Diagnosis: string | Array<CodeableConcept>
  userInteracted: boolean
  categorizedTerms?: Array<CategorizedHPOOption>
  construeResults?: {
    terms?: Array<{ id: string; label: string; description?: string }>
    message?: string
    success?: boolean
  }
}

export interface FamilyHistoryData {
  familyHistory: string
  hasSibling: boolean
  hasParent: boolean
  hasGrandparent: boolean
  hasCousin: boolean
}

export interface ConsentData {
  providerName: string
  providerEmail: string
  envelopeId: string
  status: string
  isSigningComplete: boolean
}

export interface ConsentFormData {
  medicalProfessionalName: string
  npi: string
  signatureDate: string
  signature: string
  consentedText: string
}

/** Incidental findings consent: one of Opt-In / Opt-Out, or no selection (lab default applies per copy). */
export type IncidentalFindingsSelection = 'opt-in' | 'opt-out' | null

/** NY sample retention: Opt-In checkbox only; false/undefined = unchecked (default per disclosure). */
export type NewYorkSampleRetentionSelection = boolean

/**
 * Guardian contact on legacy TRF / IMDB `requisitionFormData` (camelCase).
 * Mapped to canonical `guardianContact` via `mapRfdGuardianToCanonical` in `canonicalOrder.ts`.
 */
export interface RequisitionGuardianContact {
  contactNotAvailable?: boolean
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  phone?: string
  relationshipToPatient?: string
}

/**
 * Primary-consent signer / PCM row merged into IMDB `requisitionFormData`.
 * Used with `guardianContact` when hydrating canonical guardian from legacy JSON.
 */
export interface RequisitionPrimaryConsentFamilyMember {
  contactNotAvailable?: boolean
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  dateOfBirth?: string
  /** IMDB value e.g. `bio_mother`, `bio_father`, `legal_guardian` */
  relationship?: string
  relationshipDetail?: string | null
  status?: string
  envelopeId?: string
  isSigningComplete?: boolean
  signature?: string | null
  signatureDate?: string | null
  signerDisplayName?: string | null
}

export interface PartialRequisitionFormData {
  order?: Partial<OrderData>
  patient?: Partial<PatientData>
  clinical?: Partial<ClinicalData>
  reason?: Partial<ReasonForTestingData>
  family?: Partial<FamilyHistoryData>
  mother?: Partial<ParentFieldShape>
  father?: Partial<ParentFieldShape>
  consent?: Partial<ConsentData>
  consentFormData?: Partial<ConsentFormData>
  incidentalFindingsSelection?: IncidentalFindingsSelection
  newYorkSampleRetentionOptIn?: NewYorkSampleRetentionSelection
  /** Proband research contact opt-in; mirrors `CanonicalPatient.researchContactConsent`. */
  researchContactConsent?: boolean | null
  guardianContact?: RequisitionGuardianContact
  primaryConsentFamilyMember?: RequisitionPrimaryConsentFamilyMember
  consentFlowId?: string
  primaryConsentKey?: string
}

// Main form data interface
/**
 * @deprecated For ordering sessions, use `CanonicalOrderPayload` from `@/ordering/canonicalOrder`.
 *
 * `RequisitionFormData` is the legacy TRF (Test Requisition Form) shape. It is still used by:
 *   - `PDFRenderer.ts` for AcroForm field filling (migrate via `canonicalToPdfViewModel`)
 *   - `RequisitionFormView.tsx` for order review rendering
 *   - `cleanupPartialData` / `DatabaseOperations` for legacy submit path
 *   - `legacyTrfPayloadToCanonical` for loading old IMDB orders into canonical
 *
 * Do NOT use for new ordering-draft state. Write to `canonicalOrderSlice` directly.
 */
export interface RequisitionFormData extends PartialRequisitionFormData {
  order: OrderData
  patient: PatientData
  clinical: ClinicalData
  reason: ReasonForTestingData
  family: FamilyHistoryData
  mother?: ParentFieldShape
  father?: ParentFieldShape
  consent: ConsentData
  consentFormData?: ConsentFormData
  incidentalFindingsSelection?: IncidentalFindingsSelection
  newYorkSampleRetentionOptIn?: NewYorkSampleRetentionSelection
  guardianContact?: RequisitionGuardianContact
  primaryConsentFamilyMember?: RequisitionPrimaryConsentFamilyMember
  consentFlowId?: string
  primaryConsentKey?: string
  version: 1
}

/**
 * Per-field parent shape (flat `parent*` keys).
 *
 * Two remaining roles:
 *  1. The structural shape `FormCompletionChecker` flattens a `Comparator` into
 *     (`comparatorAsParentData`) so the per-field completeness/validity
 *     predicates can run against `comparators[]` — this is the active path.
 *  2. The inbound legacy TRF / IMDB `requisitionFormData.mother/father` shape
 *     folded into comparators by `legacyTrfPayloadToCanonical` +
 *     `normalizeInboundParentComparators` at the hydration boundary (legacy
 *     persisted orders only; GEN-3502 retires this once payloads are migrated).
 *
 * @deprecated Do not introduce new mother/father slot reads in working/UI
 *  state; iterate `CanonicalOrderPayload.comparators[]` instead.
 */
export interface ParentFieldShape {
  parentType: 'mother' | 'father'
  parentFirstName: string
  parentLastName: string
  parentDateOfBirth: string
  parentMRN: string
  parentPhone: string
  parentEmail: string
  /**
   * Sex assigned at birth. Optional because legacy parent flows derive it
   * implicitly from `parentType` (mother → female, father → male). The
   * comparator-flattened path (`comparatorAsParentData`) carries the explicit
   * `demographic.sex` here so parent-completeness stays in sync with
   * `isCanonicalComparatorComplete`, which now requires sex.
   */
  parentSex?: string | null
  phenotypicTrait: PhenotypicTrait
  hpoTerms: CategorizedHPOOption[]
  icd10Terms: string
  collectionType: CollectionType
  specimenId?: string
  demographicId?: string
  clinicalNote?: string | null
  isPrimaryGuardian?: boolean | null
  includeIncidentalFindings?: boolean | null
  newYorkSampleRetentionOptIn?: boolean | null
  researchContactConsent?: boolean | null
}

// Default values for form data
export const DEFAULT_ORDER_DATA: OrderData = {
  selectedOption: TestType.NONE,
  lab_name: '', // May be set from organization configuration or restored from older orders
}

export const DEFAULT_PATIENT_DATA: PatientData = {
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: '',
  sex: Sex.Unknown,
  mrn: '',
  collectionType: CollectionType.Unknown,
}

export const DEFAULT_CLINICAL_DATA: ClinicalData = {
  clinicalNote: '',
  differentialDiagnosis: '',
  priorGeneticTesting: '',
}

export const DEFAULT_REASON_DATA: ReasonForTestingData = {
  clinicalDiagnosis: [],
  icd10Diagnosis: '',
  userInteracted: false,
}

export const DEFAULT_FAMILY_DATA: FamilyHistoryData = {
  familyHistory: '',
  hasSibling: false,
  hasParent: false,
  hasGrandparent: false,
  hasCousin: false,
}

// P9: `DEFAULT_MOTHER_DATA` / `DEFAULT_FATHER_DATA` moved to
// `src/utils/DataCleanup.ts` (the legacy inbound/cleanup adapter, the only
// remaining consumer). Working ordering state never seeds empty parent slots
// any more — comparators[] is the source of truth.

export const DEFAULT_CONSENT_DATA: ConsentData = {
  providerName: '',
  providerEmail: '',
  envelopeId: '',
  status: '',
  isSigningComplete: false,
}

export const DEFAULT_CONSENTED_TEXT =
  "By submitting this order, I certify the following: 1) I am a licensed medical professional authorized to order genetic testing; 2) The test(s) ordered are reasonable and medically necessary for this patient's diagnosis or clinical management; 3) The patient, or their authorized decision-maker, has received appropriate information about genetic testing; 4) The patient, or their authorized decision-maker, has provided a valid email address or mobile number for follow-up related to this and future testing, when applicable; and 5) All information on this order is true and correct to the best of my knowledge."

export const DEFAULT_CONSENT_FORM_DATA: ConsentFormData = {
  medicalProfessionalName: '',
  npi: '',
  signatureDate: '',
  signature: '',
  consentedText: DEFAULT_CONSENTED_TEXT,
}

/**
 * Legacy default RFD used by the cleanup/merge adapter and a handful of
 * legacy renderer tests. P9: `mother` / `father` are intentionally NOT
 * pre-populated here — those are optional slots fed only through the inbound
 * legacy TRF adapter. Pre-seeding empty stubs would re-create the
 * two-fixed-slot mental model the migration is removing.
 */
export const DEFAULT_FORM_DATA: RequisitionFormData = {
  order: DEFAULT_ORDER_DATA,
  patient: DEFAULT_PATIENT_DATA,
  clinical: DEFAULT_CLINICAL_DATA,
  reason: DEFAULT_REASON_DATA,
  family: DEFAULT_FAMILY_DATA,
  consent: DEFAULT_CONSENT_DATA,
  consentFormData: DEFAULT_CONSENT_FORM_DATA,
  incidentalFindingsSelection: null,
  newYorkSampleRetentionOptIn: false,
  version: 1,
}

export interface PhysicianInfo {
  id: string
  name: {
    firstName: string
    lastName: string
  }
  roleTitle: string
  phone: string
  email: string
  /** National Provider Identifier when known (ordering / patient physician). */
  npi?: string
  /** US state associated with the NPI (e.g. "CA"), when resolvable. Only meaningful with an NPI. */
  npiState?: string
  /** Post-nominal credentials / titles (e.g. "MD", "DO", "MS, CGC") for the provider. */
  credentials?: string
  providerType?: string
}

export interface AuthorizingProfessionalInfo {
  npi: string
  name: string
  email?: string
}

/**
 * @deprecated For ordering sessions, use `CanonicalOrderPayload` from `@/ordering/canonicalOrder`.
 *
 * `OrderFormInfoData` was the EHR / patient header data for an order (demographics, institution,
 * providers). It is still used by:
 *   - `mapCanonicalToOrderFormInfo` as a partial read-back for display
 *   - `extractFormOrderData` for Epic SMART launch data extraction
 *   - `OrderInProgress`, `OrderResultReady`, `OrderPlacedConfirmation` display components
 *   - Legacy IMDB `json_payload.orderFormInfo` stored on old orders
 *
 * For new ordering sessions, providers + patient + institution are stored in `CanonicalOrderPayload`
 * directly. Use `initCanonicalFromOrderFormInfo` to seed canonical from EHR context at session start.
 */
export interface OrderFormInfoData {
  patient: {
    id: string
    name: {
      firstName: string
      middleName?: string
      lastName: string
      alias?: string
    }
    mrn: string
    sex: Sex
    birthDate: Date | null
    race?: string
    ethnicity?: string
  }
  institution?: {
    id: string
    name: string
    address: {
      street: string
      /** Optional second line (suite / unit); merged into TRF street when present. */
      street2?: string
      city: string
      state: string
      zip: string
      country: string
    }
  }
  orderingProvider?: PhysicianInfo
  patientPhysician?: PhysicianInfo
  medicalProfessionalInfo: AuthorizingProfessionalInfo
  /** Set when submitting on behalf of another; absent for legacy orders and when submitting as self. */
  authorizingProfessionalInfo?: AuthorizingProfessionalInfo
  /** Full physician record for the authorizing clinician when submitting on behalf of another (`PhysicianInfo`, same shape as ordering / patient physician). */
  authorizingProvider?: PhysicianInfo
  providerType?: string
  organizationName?: string
  organizationId?: string
  orderDate?: Date
  orderLastUpdateDate?: Date
  consent?: ConsentData // Add consent data for IMDB backend
  /** Epic FHIR Encounter chosen on the ready-to-submit screen (Epic SMART launches). */
  epicEncounter?: {
    id: string
    summary?: string
  }
}
